import {
  buildStatusMetricCards,
  formatRelatedEntityCountLabel,
  type MetricCardItem,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import {
  buildMetricChartSeriesFromBuckets,
  buildMetricSummaryFromBuckets,
  createDailyExecutionMetricBucket,
} from '@/features/shared/utils/chartBucketSeries';
import { resolveChartDates } from '@/features/shared/utils/chartDateWindow';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type {
  BuildStatus,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
} from '@flowqa/shared';

export type StageExecutionMetricsSummary = {
  passRateChartData: Array<Record<string, number | string | null>>;
  frequencyChartData: Array<Record<string, number | string | null>>;
  durationChartData: Array<Record<string, number | string | null>>;
  flakeScoreChartData: Array<Record<string, number | string | null>>;
  passRate: number | null;
  frequencyPerDay: number | null;
  avgDurationSeconds: number | null;
  flakeScore: number | null;
};

export const resolveStageExecutionTimestamp = (
  execution: PipelineStageExecutionListItemDto,
): Date | null => execution.lastUpdatedAt ?? execution.finishedAt ?? execution.startedAt ?? null;

export const buildStageCounts = (
  stages: PipelineStageListItemDto[] | undefined,
): Partial<Record<BuildStatus, number>> =>
  (stages ?? []).reduce<Partial<Record<BuildStatus, number>>>(
    (acc, stage) => {
      acc[stage.status] = (acc[stage.status] ?? 0) + 1;
      return acc;
    },
    {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    },
  );

export const buildStageExecutionMetricSummary = (
  executions: PipelineStageExecutionListItemDto[],
  chartDates: string[],
): StageExecutionMetricsSummary => {
  const buckets = new Map<string, ReturnType<typeof createDailyExecutionMetricBucket>>();

  [...executions]
    .sort((left, right) => {
      const leftTimestamp = resolveStageExecutionTimestamp(left);
      const rightTimestamp = resolveStageExecutionTimestamp(right);
      const timestampDelta = (leftTimestamp?.getTime() ?? 0) - (rightTimestamp?.getTime() ?? 0);
      return timestampDelta === 0 ? left.id.localeCompare(right.id) : timestampDelta;
    })
    .forEach((execution) => {
      const timestamp = resolveStageExecutionTimestamp(execution);
      if (!timestamp) return;

      const dayKey = timestamp.toISOString().slice(0, 10);
      const bucket = buckets.get(dayKey) ?? createDailyExecutionMetricBucket();
      bucket.total += 1;
      if (execution.status === 'completed') {
        bucket.completed += 1;
      }
      if (execution.status === 'failed') {
        bucket.failed += 1;
      }
      bucket.flakeOutcomes.push(execution.status === 'completed' ? 'passed' : 'notPassed');
      if (typeof execution.durationMs === 'number') {
        const durationSeconds = execution.durationMs / 1000;
        bucket.durationSumSeconds += durationSeconds;
        bucket.durationCount += 1;
      }
      buckets.set(dayKey, bucket);
    });

  const orderedDates = resolveChartDates(chartDates, buckets.keys());
  const { passRateChartData, frequencyChartData, durationChartData, flakeScoreChartData } =
    buildMetricChartSeriesFromBuckets(buckets, orderedDates);
  const summary = buildMetricSummaryFromBuckets(buckets, orderedDates);

  return {
    passRateChartData,
    frequencyChartData,
    durationChartData,
    flakeScoreChartData,
    passRate: summary.passRate,
    frequencyPerDay: summary.frequencyPerDay,
    avgDurationSeconds: summary.avgDurationSeconds,
    flakeScore: summary.flakeScore,
  };
};

export const buildFallbackStageExecutions = (
  stages: PipelineStageListItemDto[],
): PipelineStageExecutionListItemDto[] =>
  stages.flatMap((stage) =>
    (stage.executionSummaries ?? []).map((summary) => ({
      id: summary.id,
      projectId: stage.projectId,
      pipelineId: stage.pipelineId,
      pipelineExecutionId: summary.id,
      organisationId: stage.organisationId,
      createdById: '',
      pipelineName: stage.pipelineName,
      pipelineUrl: stage.pipelineUrl ?? null,
      stageName: stage.stageName,
      buildKey: summary.buildKey ?? null,
      isLatest: stage.latestRun?.id === summary.id,
      tags: stage.tags ?? [],
      status: summary.status,
      lifecycleState: summary.lifecycleState ?? null,
      conclusion: summary.conclusion ?? null,
      providerStatus: summary.providerStatus ?? null,
      providerConclusion: summary.providerConclusion ?? null,
      requiresManualAction: summary.requiresManualAction ?? null,
      startedAt: null,
      finishedAt: null,
      durationMs:
        stage.latestRun?.id === summary.id
          ? (stage.latestRun.durationMs ?? null)
          : (stage.metrics?.averageDurationMs ?? null),
      jobTotal: undefined,
      jobStatusCounts: undefined,
      jobSummaries: undefined,
      lastUpdatedAt: summary.timestamp ?? null,
      deletedAt: stage.deletedAt,
    })),
  );

export const buildStageStatusCountsByDate = (
  executions: PipelineStageExecutionListItemDto[],
): ReadonlyMap<string, Record<BuildStatus, number>> => {
  const buckets = new Map<string, Record<BuildStatus, number>>();
  executions.forEach((execution) => {
    const timestamp = resolveStageExecutionTimestamp(execution);
    if (!timestamp) return;
    const dayKey = timestamp.toISOString().slice(0, 10);
    const bucket = buckets.get(dayKey) ?? {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };
    bucket[execution.status] += 1;
    buckets.set(dayKey, bucket);
  });
  return buckets;
};

export const buildStageMetricCards = ({
  options,
  counts,
  deltas,
  stageTotal,
  executionTotal,
  previousCounts,
  previousStageTotal,
  previousExecutionTotal,
  loading,
  showDelta,
}: {
  options: Array<MetricCardOption<BuildStatus>>;
  counts: Partial<Record<BuildStatus, number>>;
  deltas?: Partial<Record<BuildStatus, number>>;
  stageTotal: number;
  executionTotal: number;
  previousCounts?: Partial<Record<BuildStatus, number>>;
  previousStageTotal?: number;
  previousExecutionTotal?: number;
  loading: boolean;
  showDelta: boolean;
}): MetricCardItem[] => {
  const cards = buildStatusMetricCards({
    options,
    counts,
    deltas,
    total: stageTotal,
    previousCounts,
    previousTotal: previousStageTotal,
    loading,
    showDelta,
  });
  const [totalCard, ...statusCards] = cards;
  const executionDelta =
    showDelta && previousExecutionTotal !== undefined
      ? executionTotal - previousExecutionTotal
      : undefined;
  const hasNoExecutionData = executionTotal === 0 && (previousExecutionTotal ?? 0) === 0;
  const executionPercentageDelta =
    showDelta && previousExecutionTotal !== undefined && previousExecutionTotal > 0
      ? Math.round(
          ((executionTotal - previousExecutionTotal) / previousExecutionTotal) * 100 * 100,
        ) / 100
      : undefined;

  let resolvedExecutionDelta: string | number | undefined;
  if (hasNoExecutionData) {
    resolvedExecutionDelta = undefined;
  } else if (executionDelta === undefined || executionDelta === 0) {
    resolvedExecutionDelta = showDelta ? '-' : undefined;
  } else {
    resolvedExecutionDelta = executionDelta > 0 ? `+${executionDelta}` : executionDelta;
  }
  const executionDeltaTone =
    resolvedExecutionDelta === undefined || resolvedExecutionDelta === '-'
      ? 'neutral'
      : (executionDelta ?? 0) > 0
        ? 'positive'
        : 'negative';
  const resolvedExecutionPercentageDelta =
    executionPercentageDelta === undefined || executionPercentageDelta === 0
      ? undefined
      : executionPercentageDelta > 0
        ? `+${executionPercentageDelta}%`
        : `${executionPercentageDelta}%`;
  const executionPercentageDeltaTone =
    resolvedExecutionPercentageDelta === undefined
      ? 'neutral'
      : (executionPercentageDelta ?? 0) > 0
        ? 'positive'
        : 'negative';

  return [
    {
      ...totalCard,
      status: 'Stage Runs',
      count: executionTotal.toLocaleString(),
      secondaryValue: formatRelatedEntityCountLabel(stageTotal, 'Stage'),
      delta: resolvedExecutionDelta,
      deltaTone: executionDeltaTone,
      countDelta: resolvedExecutionDelta,
      countDeltaTone: executionDeltaTone,
      percentage: undefined,
      percentageDelta: resolvedExecutionPercentageDelta,
      percentageDeltaTone: executionPercentageDeltaTone,
    },
    ...statusCards,
  ];
};

export const sortHistoryRows = (
  rows: EntityDailyHistoryRow[],
  sortKey: DailyHistorySortKey,
  sortDirection: 'asc' | 'desc',
): EntityDailyHistoryRow[] =>
  [...rows].sort((left, right) => {
    const leftValue = (sortKey === 'prefixLabel' ? left.prefixLabel : left.name) ?? '';
    const rightValue = (sortKey === 'prefixLabel' ? right.prefixLabel : right.name) ?? '';
    const result = leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' });
    if (result !== 0) {
      return sortDirection === 'asc' ? result : -result;
    }
    const fallbackResult = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
    });
    return sortDirection === 'asc' ? fallbackResult : -fallbackResult;
  });
